"use client";

import { useEffect, useLayoutEffect } from "react";
import { cleanupFireworks, initFireworks, installAvatarCursorShadowPatch } from "../../fireworks";

// TODO: Remove this app-side workaround after Firework fixes the SDK restore bug.
//
// Root cause:
// Firework SDK v20260602 restores an open soft-PIP/modal from browser storage on reload.
// In some reload flows, its stored modal state (fw_ply_sft_pip) and global player state (fw_gs)
// disagree about whether the video is muted. The SDK then briefly replays mute/unmute state,
// sending multiple vid:mute/vid:unmute pings and sometimes restoring the modal without the
// unmute control.
//
// Workaround:
// Normalize Firework's stored restore state before the SDK reads it. This keeps the modal
// restore behavior intact, but forces a stable muted start state with the unmute control visible.
// This should be owned and fixed by Firework in the SDK; we should not need this patch long term.
const useBrowserLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

function patchStoredJson(storage: Storage, key: string, patch: (value: unknown) => boolean): void {
  const raw = storage.getItem(key);
  if (!raw) return;

  try {
    const value = JSON.parse(raw) as unknown;
    if (patch(value)) {
      storage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // Ignore malformed SDK state. Firework can regenerate it.
  }
}

function patchObjectTree(value: unknown, patch: (value: Record<string, unknown>) => boolean) {
  if (!value || typeof value !== "object") return false;

  const target = value as Record<string, unknown>;
  let changed = patch(target);

  Object.keys(target).forEach((key) => {
    const child = target[key];
    if (child && typeof child === "object") {
      changed = patchObjectTree(child, patch) || changed;
    }
  });

  return changed;
}

function patchFireworkRestoreState(): void {
  if (typeof window === "undefined" || !/\/live(?:\/|$)/.test(window.location.pathname)) return;

  // fw_gs can contain stale per-player/video state from the previous page session.
  // Only volatile state is cleared here; we avoid changing unrelated Firework settings.
  const patchGlobalState = (value: unknown) =>
    patchObjectTree(value, (target) => {
      let changed = false;

      if (Object.hasOwn(target, "playerStates")) {
        target.playerStates = {};
        changed = true;
      }

      if (Object.hasOwn(target, "isUserMuted") && target.isUserMuted !== true) {
        target.isUserMuted = true;
        changed = true;
      }

      return changed;
    });

  // fw_ply_sft_pip is the key that keeps the same modal/video open across reloads.
  // Do not delete it. Deleting it would stop modal restore and force users to find the video again.
  // Instead, make the restored player state explicit: start muted and keep unmute UI enabled.
  const patchSoftPipState = (value: unknown) => {
    if (!value || typeof value !== "object") return false;

    const target = value as Record<string, unknown>;
    let changed = false;

    if (target.muted !== true) {
      target.muted = true;
      changed = true;
    }
    if (target.player_floating_unmute !== true) {
      target.player_floating_unmute = false;
      changed = true;
    }
    if (target.player_unmute !== true) {
      target.player_unmute = true;
      changed = true;
    }
    if (target.player_tap_to_unmute !== true) {
      target.player_tap_to_unmute = true;
      changed = true;
    }

    if (target.player_controls_hide === true) {
      target.player_controls_hide = false;
      changed = true;
    }

    return changed;
  };

  [window.localStorage, window.sessionStorage].forEach((storage) => {
    // Firework may use either storage depending on how the modal was opened.
    patchStoredJson(storage, "fw_gs", patchGlobalState);
    patchStoredJson(storage, "fw_ply_sft_pip", patchSoftPipState);
  });
}

export default function FireworkInit() {
  useBrowserLayoutEffect(() => {
    patchFireworkRestoreState();
    installAvatarCursorShadowPatch();
  }, []);

  useEffect(() => {
    initFireworks();
    return () => {
      cleanupFireworks();
    };
  }, []);

  return <></>;
}
